import { layoutNodes } from "@/app/(v2)/workflows/_hooks/useLayout";
import {
  buildWorkflowGraphLookups,
  collectAllLoopBodyDescendantIds,
} from "@/app/(v2)/workflows/_lib/workflowGraphAnalysis";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { getWorkflowVerticalGap } from "@/lib/workflow/layoutPolicy";
import type { AIWorkflow } from "@/types/aiChat";
import type { Edge, Node, ReactFlowInstance } from "@xyflow/react";
import { toast } from "@/lib/toast";

import { getCompactPreference } from "@/lib/workflow/compactPreference";
import {
  STREAMING_X_CENTER,
  getBranchingSourceHandle,
  hasWaitBranchChildren,
  type ConnectorSource,
  applyConnectorsToNodes,
} from "./workflowTransform";

// Backend returns this format when the async AI title hasn't been generated
// yet (see buildWorkflowEntityWithMeta in framework_agent_response.go). The
// FE polls for the real name later — don't let the placeholder clobber a
// good local name during AI update streams.
export const DEFAULT_TIMESTAMP_NAME_RE = /^Workflow \w+ \d{1,2}, \d{1,2}:\d{2} [AP]M$/;

/**
 * Refine/update canvas nodes with the final workflow data from the done event.
 *
 * The stream sends preliminary nodes that may have incomplete/incorrect data.
 * The final "done" event contains the fully validated workflow.
 *
 * This function CLEARS the canvas and REBUILDS it entirely from the final data.
 * This ensures correct node types, payloads, and connectors are displayed.
 *
 * @param finalWorkflow - The complete workflow from the done event
 * @param definitions - Action/trigger definitions for enrichment
 * @param setNodes - React Flow setNodes function
 * @param setEdges - React Flow setEdges function
 * @param callbacks - Optional callbacks for updating workflow name/description
 * @param _reactFlowInstance - Reserved for backward-compatible callers
 * @param savedConnectors - Optional map of saved connectors to restore (captured before streaming cleared nodes)
 */
export function refineCanvasWithFinalWorkflow(
  finalWorkflow: AIWorkflow,
  definitions: ActionTriggerDefinition[],
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void,
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void,
  callbacks?: {
    setWorkflowName?: (name: string) => void;
    setWorkflowDescription?: (description: string) => void;
  },
  _reactFlowInstance?: ReactFlowInstance | null,
  placeholderCallbacks?: {
    onDropAction?: (placeholderId: string, dragData: any) => void;
    onShowActionModal?: (placeholderId: string) => void;
    onShowLogicModal?: (placeholderId: string) => void;
  },
  connectorSource?: ConnectorSource,
  savedConnectors?: Map<string, any>,
): void {
  console.log(
    "[refineCanvasWithFinalWorkflow] Called with placeholderCallbacks:",
    !!placeholderCallbacks,
    "onDropAction:",
    !!placeholderCallbacks?.onDropAction,
  );

  // Update workflow name and description if callbacks provided
  if (callbacks?.setWorkflowName && finalWorkflow.name && !DEFAULT_TIMESTAMP_NAME_RE.test(finalWorkflow.name)) {
    callbacks.setWorkflowName(finalWorkflow.name);
  }
  if (callbacks?.setWorkflowDescription && finalWorkflow.description) {
    callbacks.setWorkflowDescription(finalWorkflow.description);
  }
  const finalNodes = finalWorkflow.definition.nodes;

  // Check if we have saved positions from uiMetadata
  const savedPositions = finalWorkflow.uiMetadata?.nodePositions;
  const hasSavedPositions = savedPositions && Object.keys(savedPositions).length > 0;

  // Helper to determine node type for React Flow (trigger vs action vs logic)
  const getNodeType = (nodeKey: string, nodeData: any): string => {
    if (nodeKey === "root") return "trigger";

    // Check if this is a logic action by looking up in definitions
    const definition = definitions.find(def => def.type === nodeData.type);
    if (definition?.category === "logic") {
      return "logic";
    }

    return "action";
  };

  // Build fresh nodes from the final workflow data (with temporary positions)
  let freshNodes: Node[] = [];
  const isCompact = getCompactPreference();
  const ySpacing = isCompact ? 170 : 220;
  let placeholderCounter = 0;

  // Generate a unique refresh key to force React to re-render node components
  const refreshKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  for (const [nodeId, nodeData] of Object.entries(finalNodes)) {
    // Use saved position if available, otherwise use temporary position (will be recalculated)
    const position = hasSavedPositions
      ? (savedPositions[nodeId] as { x: number; y: number }) || { x: STREAMING_X_CENTER, y: 0 }
      : { x: STREAMING_X_CENTER, y: 0 };
    const nodeType = getNodeType(nodeId, nodeData);
    const definition = definitions.find(d => d.type === nodeData.type);

    freshNodes.push({
      id: nodeId,
      type: nodeType,
      position,
      draggable: true,
      data: {
        type: nodeData.type,
        name: definition?.name || nodeData.type,
        description: (nodeData as any).description || "",
        platform: definition?.platform || "",
        payload: nodeData.payload ? { ...nodeData.payload } : {},
        connector: nodeData.connector,
        branch: nodeData.branch,
        children: nodeData.children,
        loopBody: nodeData.loopBody,
        isStreaming: false,
        _refreshKey: refreshKey,
      },
    });
  }

  // Tag wait nodes that have branched children (resumed/timed_out) so the
  // LogicNode component renders side handles instead of a single bottom handle.
  const freshNodeMap = new Map(freshNodes.map(n => [n.id, n]));
  for (const [nodeId, nodeData] of Object.entries(finalNodes)) {
    if (hasWaitBranchChildren(nodeData, finalNodes)) {
      const waitNode = freshNodeMap.get(nodeId);
      if (waitNode) {
        waitNode.data = { ...waitNode.data, hasWaitBranches: true };
      }
    }
  }

  // Build fresh edges from the final workflow data (node-to-node edges only first)
  const freshEdges: Edge[] = [];

  for (const [nodeId, nodeData] of Object.entries(finalNodes)) {
    const nodeType = getNodeType(nodeId, nodeData);
    const isBranchingNode = nodeData.type === "if" || nodeData.type === "wait";
    const isForEachNode = nodeType === "logic" && nodeData.type === "for-each";

    if (nodeData.children && nodeData.children.length > 0) {
      for (const childId of nodeData.children) {
        const childNodeData = finalNodes[childId];
        const sourceHandle = isBranchingNode ? getBranchingSourceHandle(nodeData.type, childNodeData) : undefined;
        const isTargetForEach = childNodeData?.type === "for-each";

        const edgeData: Record<string, boolean> = {};
        if (isTargetForEach) edgeData.isTargetForEach = true;
        if (isForEachNode) edgeData.isOnComplete = true;

        freshEdges.push({
          id: `${nodeId}=>${childId}`,
          source: nodeId,
          target: childId,
          type: "workflow",
          ...(sourceHandle && { sourceHandle }),
          ...(Object.keys(edgeData).length > 0 && { data: edgeData }),
        });
      }
    }

    // Create edges for loopBody nodes (for-each loop body)
    if (nodeData.loopBody && nodeData.loopBody.length > 0) {
      for (const lbId of nodeData.loopBody) {
        const lbNodeData = finalNodes[lbId];
        const isTargetForEach = lbNodeData?.type === "for-each";

        freshEdges.push({
          id: `${nodeId}=>${lbId}`,
          source: nodeId,
          target: lbId,
          type: "workflow",
          ...(isTargetForEach && { data: { isTargetForEach: true } }),
        });
      }
    }
  }

  // If no saved positions, use layoutNodes for consistent tree layout
  if (!hasSavedPositions) {
    freshNodes = layoutNodes(freshNodes, freshEdges, { verticalGap: getWorkflowVerticalGap(isCompact) });
  }

  const { childrenMap, onCompleteTargetIds, forEachNodes } = buildWorkflowGraphLookups(freshNodes, freshEdges);
  const loopBodyDescendantIds = collectAllLoopBodyDescendantIds(forEachNodes, childrenMap, onCompleteTargetIds, {
    excludePlaceholders: true,
  });

  // Now add placeholders for leaf nodes (after layout is calculated)
  const nodesWithPlaceholders: Node[] = [...freshNodes];
  for (const [nodeId, nodeData] of Object.entries(finalNodes)) {
    const hasChildren = nodeData.children && nodeData.children.length > 0;
    const hasLoopBody = nodeData.loopBody && nodeData.loopBody.length > 0;
    const isLoopBodyDescendant = loopBodyDescendantIds.has(nodeId);
    if (!hasChildren && !hasLoopBody && !isLoopBodyDescendant) {
      const parentNode = freshNodes.find(n => n.id === nodeId);
      if (parentNode) {
        const placeholderId = `placeholder-${nodeId}-${placeholderCounter++}`;
        console.log(
          "[refineCanvasWithFinalWorkflow] Creating placeholder:",
          placeholderId,
          "with callbacks:",
          !!placeholderCallbacks?.onDropAction,
        );
        nodesWithPlaceholders.push({
          id: placeholderId,
          type: "placeholder",
          position: { x: parentNode.position.x, y: parentNode.position.y + ySpacing },
          data: {
            // Include placeholder callbacks if provided
            ...(placeholderCallbacks?.onDropAction && { onDropAction: placeholderCallbacks.onDropAction }),
            ...(placeholderCallbacks?.onShowActionModal && {
              onShowActionModal: placeholderCallbacks.onShowActionModal,
            }),
            ...(placeholderCallbacks?.onShowLogicModal && { onShowLogicModal: placeholderCallbacks.onShowLogicModal }),
          },
          draggable: false,
        });

        // Add edge to placeholder
        freshEdges.push({
          id: `${nodeId}=>${placeholderId}`,
          source: nodeId,
          target: placeholderId,
          type: "smoothstep",
          style: {
            stroke: "#3b82f6",
            strokeWidth: 2,
            strokeDasharray: "5,5",
          },
        });
      }
    }
  }

  // Update freshNodes to include placeholders
  freshNodes = nodesWithPlaceholders;

  // Replace all nodes with fresh data while preserving connectors
  setNodes(currentNodes => {
    // Get connectors to preserve:
    // - Use savedConnectors if provided (captured before streaming cleared nodes)
    // - Otherwise fall back to currentNodes (for non-streaming scenarios)
    const connectorsToPreserve =
      savedConnectors && savedConnectors.size > 0
        ? savedConnectors
        : new Map(currentNodes.filter(n => n.data?.connector).map(n => [n.id, n.data.connector]));

    // Apply preserved connectors to fresh nodes
    const nodesWithConnectors = freshNodes.map(freshNode => {
      const preservedConnector = connectorsToPreserve.get(freshNode.id);
      if (preservedConnector) {
        return {
          ...freshNode,
          data: { ...freshNode.data, connector: preservedConnector },
        };
      }
      return freshNode;
    });

    // Apply auto-selection for nodes without connectors (if source provided)
    return connectorSource
      ? applyConnectorsToNodes(nodesWithConnectors, definitions, connectorSource)
      : nodesWithConnectors;
  });

  // Replace edges with fresh data
  setEdges(freshEdges);
}

/**
 * Determine if the workflow structure has changed (nodes added/removed).
 * Uses saved node IDs from before streaming started for accurate comparison.
 * Returns true if structure changed, false if only data/payload changed.
 */
function hasStructuralChanges(savedNodeIds: Set<string>, finalNodes: Record<string, any>): boolean {
  // Get final node IDs
  const finalNodeIds = new Set(Object.keys(finalNodes));

  // Check if node sets are different
  if (savedNodeIds.size !== finalNodeIds.size) {
    console.log("[hasStructuralChanges] Size mismatch - saved:", savedNodeIds.size, "final:", finalNodeIds.size);
    return true;
  }

  for (const id of savedNodeIds) {
    if (!finalNodeIds.has(id)) {
      console.log("[hasStructuralChanges] Missing node in final:", id);
      return true;
    }
  }

  for (const id of finalNodeIds) {
    if (!savedNodeIds.has(id)) {
      console.log("[hasStructuralChanges] New node in final:", id);
      return true;
    }
  }

  return false;
}

/**
 * Smart update function that preserves node positions when only data/payload changes.
 * Falls back to full rebuild when structure changes (nodes added/removed).
 *
 * @param finalWorkflow - The final AI workflow data
 * @param definitions - Action/trigger definitions
 * @param setNodes - React Flow setNodes function
 * @param setEdges - React Flow setEdges function
 * @param callbacks - Optional callbacks for updating workflow name/description
 * @param _reactFlowInstance - Reserved for backward-compatible callers
 * @param placeholderCallbacks - Optional callbacks for placeholder nodes
 * @param connectorSource - Optional connector source for auto-selection
 * @param savedConnectors - Optional map of saved connectors to restore
 * @param savedPositions - Optional map of saved positions from before streaming (for restoration)
 * @param savedNodeIds - Optional set of node IDs from before streaming (for structural change detection)
 * @returns true if structure changed (full rebuild), false if only data updated
 */
export function updateCanvasWithWorkflowChanges(
  finalWorkflow: AIWorkflow,
  definitions: ActionTriggerDefinition[],
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void,
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void,
  callbacks?: {
    setWorkflowName?: (name: string) => void;
    setWorkflowDescription?: (description: string) => void;
  },
  _reactFlowInstance?: ReactFlowInstance | null,
  placeholderCallbacks?: {
    onDropAction?: (placeholderId: string, dragData: any) => void;
    onShowActionModal?: (placeholderId: string) => void;
    onShowLogicModal?: (placeholderId: string) => void;
  },
  connectorSource?: ConnectorSource,
  savedConnectors?: Map<string, any>,
  savedPositions?: Map<string, { x: number; y: number }>,
  savedNodeIds?: Set<string>,
): boolean {
  const finalNodes = finalWorkflow.definition.nodes;

  // If we don't have saved node IDs, we can't detect structural changes - do full rebuild
  if (!savedNodeIds || savedNodeIds.size === 0) {
    console.log("[updateCanvasWithWorkflowChanges] No saved node IDs, doing full rebuild");
    refineCanvasWithFinalWorkflow(
      finalWorkflow,
      definitions,
      setNodes,
      setEdges,
      callbacks,
      _reactFlowInstance,
      placeholderCallbacks,
      connectorSource,
      savedConnectors,
    );
    return true;
  }

  // Check if structure changed using saved node IDs (from before streaming started)
  const structureChanged = hasStructuralChanges(savedNodeIds, finalNodes);

  console.log(
    "[updateCanvasWithWorkflowChanges] Structure changed:",
    structureChanged,
    "Saved nodes:",
    savedNodeIds.size,
    "Final nodes:",
    Object.keys(finalNodes).length,
  );

  if (structureChanged) {
    // Fall back to full rebuild
    console.log("[updateCanvasWithWorkflowChanges] Falling back to full rebuild due to structural changes");
    refineCanvasWithFinalWorkflow(
      finalWorkflow,
      definitions,
      setNodes,
      setEdges,
      callbacks,
      _reactFlowInstance,
      placeholderCallbacks,
      connectorSource,
      savedConnectors,
    );
    return true;
  }

  // Update workflow name and description if callbacks provided
  if (callbacks?.setWorkflowName && finalWorkflow.name && !DEFAULT_TIMESTAMP_NAME_RE.test(finalWorkflow.name)) {
    callbacks.setWorkflowName(finalWorkflow.name);
  }
  if (callbacks?.setWorkflowDescription && finalWorkflow.description) {
    callbacks.setWorkflowDescription(finalWorkflow.description);
  }

  // Generate a unique refresh key to force React to re-render node components
  const refreshKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Update node data while RESTORING original positions from savedPositions
  setNodes(nodes =>
    nodes.map(node => {
      // Skip placeholders
      if (node.type === "placeholder") {
        // But restore placeholder position based on parent if we have saved positions
        return node;
      }

      const finalNodeData = finalNodes[node.id];
      if (!finalNodeData) {
        return node;
      }

      const definition = definitions.find(d => d.type === finalNodeData.type);

      // Preserve connector from saved connectors or current node
      const preservedConnector = savedConnectors?.get(node.id) || node.data?.connector;
      const finalConnector = finalNodeData.connector || preservedConnector;

      // RESTORE position from saved positions (before streaming cleared them)
      const restoredPosition = savedPositions?.get(node.id) || node.position;

      // Detect if this is a wait node with branched children
      const isWaitWithBranches = hasWaitBranchChildren(finalNodeData, finalNodes);

      return {
        ...node,
        position: restoredPosition,
        data: {
          ...node.data,
          type: finalNodeData.type,
          name: definition?.name || finalNodeData.type,
          description: (finalNodeData as any).description || "",
          platform: definition?.platform || "",
          payload: finalNodeData.payload ? { ...finalNodeData.payload } : {},
          connector: finalConnector,
          branch: finalNodeData.branch,
          children: finalNodeData.children,
          loopBody: finalNodeData.loopBody,
          isStreaming: false,
          _refreshKey: refreshKey,
          hasWaitBranches: isWaitWithBranches,
        },
      };
    }),
  );

  console.log("[updateCanvasWithWorkflowChanges] Updated node data and restored positions");

  // Show success message
  toast.success("Workflow updated");

  return false;
}
